import { nip04Service } from './nip04Service';
import { blossomService } from './blossomService';

/**
 * Media Service - Unified Nostr Media Upload (NIP-96 / NIP-98)
 * Primary: NIP-96 (Standard HTTP File Storage)
 * Fallback: Blossom (Blob Storage)
 */
export class MediaService {
    private readonly NIP96_SERVERS = [
        'https://nostr.build',
        'https://void.cat',
        'https://nostrimg.com'
    ];

    /**
     * Create NIP-98 Authentication Header
     */
    private async createNIP98Header(
        url: string,
        method: string,
        loginMode: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        const eventTemplate = {
            kind: 27235,
            content: '',
            created_at: Math.floor(Date.now() / 1000),
            tags: [
                ['u', url],
                ['method', method],
            ],
        };

        const signedEvent = await nip04Service.signEvent(eventTemplate, loginMode, sk);
        return `Nostr ${btoa(JSON.stringify(signedEvent))}`;
    }

    /**
     * Upload via Blossom (Fallback)
     */
    async uploadBlossom(
        file: File,
        loginMode: 'extension' | 'seed' | 'read_only',
        sk?: Uint8Array
    ): Promise<string> {
        return blossomService.uploadFile(file, loginMode, sk);
    }

    /**
     * Upload via NIP-96 (Standard) with Blossom Fallback
     */
    async uploadFile(
        file: File,
        loginMode: 'extension' | 'seed' | 'read_only',
        sk?: Uint8Array
    ): Promise<string> {
        if (loginMode === 'read_only') throw new Error('Yalnızca okuma modunda dosya yüklenemez.');

        // 1. Try NIP-96 Servers
        for (const serverBase of this.NIP96_SERVERS) {
            try {
                console.log(`📤 [MediaService] Discovering NIP-96 on ${serverBase}...`);

                // Discovery
                const configRes = await fetch(`${serverBase}/.well-known/nostr/nip96.json`);
                if (!configRes.ok) continue;

                const config = await configRes.json();
                const uploadUrl = config.api_url;

                // Auth Header
                const authHeader = await this.createNIP98Header(uploadUrl, 'POST', loginMode as any, sk);

                // FormData
                const formData = new FormData();
                formData.append('file', file);
                formData.append('caption', file.name);

                console.log(`📤 [MediaService] Uploading to ${uploadUrl}...`);

                const uploadRes = await fetch(uploadUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': authHeader
                    },
                    body: formData
                });

                if (uploadRes.ok) {
                    const data = await uploadRes.json();
                    if (data.status === 'success' && data.nip94_event) {
                        const urlTag = data.nip94_event.tags.find((t: any[]) => t[0] === 'url');
                        if (urlTag) return urlTag[1];
                    }
                    return data.url || data.nip94_event?.tags.find((t: any[]) => t[0] === 'url')?.[1];
                }
            } catch (error) {
                console.warn(`⚠️ [MediaService] Error on ${serverBase}:`, error);
            }
        }

        // 2. Fallback to Blossom if NIP-96 fails
        try {
            console.log(`🔄 [MediaService] All NIP-96 failed, falling back to Blossom...`);
            return await this.uploadBlossom(file, loginMode, sk);
        } catch (blossomError) {
            console.error(`❌ [MediaService] Blossom fallback also failed:`, blossomError);
            throw new Error('Dosya yüklenemedi. Hem NIP-96 hem de Blossom sunucuları başarısız oldu.');
        }
    }

    /**
     * @deprecated Use uploadFile instead
     */
    async uploadNIP96(file: File, loginMode: 'extension' | 'seed' | 'read_only', sk?: Uint8Array) {
        return this.uploadFile(file, loginMode, sk);
    }

    /**
     * Detect if content is image
     */
    isImageUrl(url: string): boolean {
        return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url) || url.includes('nostr.build') || url.includes('blossom');
    }
}

export const mediaService = new MediaService();
